/**
 * Candidate-side talent network API (PRD §8.5): the connect-join dialog's
 * submit, and the candidate's own memberships (My Jobs, audit E03). The submit
 * endpoint is idempotent per (company, candidate): a repeat submit while active
 * returns the existing row with `created: false`, which the dialog renders as
 * its "already a member" state.
 */
import type { SubmissionReceiptFields } from '@tabbio/types';
import { api } from './api';
import { ApplyRequestError } from './apply-error';

export interface TalentNetworkCompanySummary {
  id: string;
  name: string;
  slug: string | null;
  logo: string | null;
}

/**
 * `receipts` / `receiptsStatus` arrive (optional, audit E02) on the
 * candidate's own list (`GET /api/talent-network/mine`), not on submit.
 */
export interface TalentNetworkSubmission extends SubmissionReceiptFields {
  id: string;
  company: TalentNetworkCompanySummary;
  status: string;
  source: string;
  cvId: string | null;
  submittedCv: { id: string | null; title: string | null } | null;
  consentVersion: string | null;
  submittedAt: string;
  updatedAt: string;
  withdrawnAt: string | null;
}

export interface TalentNetworkSubmitInput {
  companySlug: string;
  cvId?: string;
  cvSelection?: import('@tabbio/types').CvDocumentSelection;
  consent: {
    version: string;
    accepted: true;
    /** The exact localized consent sentence shown on screen. */
    text?: string;
  };
}

export interface TalentNetworkSubmitResult {
  submission: TalentNetworkSubmission;
  /** False when the candidate was already active in this network. */
  created: boolean;
}

export async function submitTalentNetwork(
  input: TalentNetworkSubmitInput,
): Promise<TalentNetworkSubmitResult> {
  const res = await api.post<TalentNetworkSubmitResult>(
    '/api/talent-network/submit',
    input,
  );

  if (res.error) {
    // Keep the code: the join sheets translate the known ones (X4).
    throw new ApplyRequestError(
      res.error.code,
      res.error.message,
      res.error.details,
    );
  }

  if (!res.data) {
    throw new Error('Talent network submission returned no data');
  }

  return res.data;
}

/**
 * The signed-in candidate's talent network memberships, newest first, each
 * with what it sent (`receipts`, audit E02/E03). Only the caller's own rows.
 */
export async function fetchMyTalentNetworks(): Promise<TalentNetworkSubmission[]> {
  const res = await api.get<{ submissions: TalentNetworkSubmission[] }>(
    '/api/talent-network/mine',
  );

  if (res.error) {
    throw new Error(res.error.message);
  }

  return Array.isArray(res.data?.submissions) ? res.data.submissions : [];
}
